import { useState } from "react";
import { Link } from "react-router-dom";
import { Wine, Sparkles, ArrowRight } from "lucide-react";
import client, { API, streamTokens } from "../lib/api";
import { cleanMd } from "./ChatWidget";
import { trackEvent } from "../lib/tracking";
import { Reveal } from "./Reveal";

const BUDGETS = ["Fino a 15 €", "15–30 €", "30–60 €", "Oltre 60 €"];

export default function WineAdvisor() {
  const [form, setForm] = useState({ dish: "", occasion: "", budget: "" });
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lead, setLead] = useState({ name: "", email: "", privacy: false });
  const [leadState, setLeadState] = useState("idle"); // idle | loading | done | error

  const ask = async (e) => {
    e.preventDefault();
    if (!form.dish.trim() || loading) return;
    setResult("");
    setError("");
    setLoading(true);
    trackEvent("wine_advisor_submit", { budget: form.budget });
    try {
      const res = await fetch(`${API}/consiglia-vino`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      await streamTokens(res, (token) => setResult((r) => r + token));
    } catch {
      setError("Il consigliere non risponde. Riprova tra poco.");
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (leadState === "loading") return;
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email.trim());
    if (!lead.privacy || lead.name.trim().length < 2 || !emailOk) {
      setLeadState("error");
      return;
    }
    setLeadState("loading");
    trackEvent("consigliere_lead_submit", { budget: form.budget });
    try {
      await client.post("/leads", { ...lead, source: "consigliere-vino" });
      setLeadState("done");
    } catch {
      setLeadState("error");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
      <Reveal>
        <div className="flex items-center gap-3 mb-5">
          <Wine size={22} className="text-oro" />
          <p className="text-oro text-xs uppercase tracking-[0.35em]">Interattivo · AI</p>
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl font-bold text-panna leading-tight">
          Consigliami un vino
        </h2>
        <p className="mt-5 text-panna/70 leading-relaxed">
          Dimmi cosa mangi, l'occasione e il budget: l'assistente di Dentro il Calice ti risponde
          come farebbe Stefano al tavolo — senza paroloni.
        </p>
        <form onSubmit={ask} data-testid="wine-advisor-form" className="mt-8 space-y-4">
          <input
            data-testid="wine-dish"
            required
            minLength={2}
            placeholder="Cosa mangi? (es. tagliata al rosmarino)"
            value={form.dish}
            onChange={(e) => setForm({ ...form, dish: e.target.value })}
            className="w-full rounded-lg px-4 py-3.5 bg-nero-light border border-panna/15 text-panna placeholder:text-panna/35 focus:outline-none focus:ring-2 focus:ring-oro/50"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              data-testid="wine-occasion"
              placeholder="Occasione (es. cena tra amici)"
              value={form.occasion}
              onChange={(e) => setForm({ ...form, occasion: e.target.value })}
              className="w-full rounded-lg px-4 py-3.5 bg-nero-light border border-panna/15 text-panna placeholder:text-panna/35 focus:outline-none focus:ring-2 focus:ring-oro/50"
            />
            <select
              data-testid="wine-budget"
              value={form.budget}
              onChange={(e) => setForm({ ...form, budget: e.target.value })}
              className="w-full rounded-lg px-4 py-3.5 bg-nero-light border border-panna/15 text-panna focus:outline-none focus:ring-2 focus:ring-oro/50"
            >
              <option value="">Budget a bottiglia</option>
              {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <button
            type="submit"
            data-testid="wine-advisor-submit"
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-full bg-bordeaux px-8 py-4 font-medium text-panna border border-oro/30 hover:bg-bordeaux-dark transition-colors disabled:opacity-60"
          >
            <Sparkles size={16} />
            {loading ? "Sto pensando al calice giusto…" : "Consigliami"}
          </button>
        </form>
      </Reveal>
      <Reveal delay={0.15}>
        <div data-testid="wine-advisor-result" className="rounded-2xl border border-oro/25 bg-nero-light p-8 min-h-[16rem]">
          {error && <p className="text-oro-light text-sm">{error}</p>}
          {!result && !loading && !error && (
            <div className="h-full flex flex-col items-center justify-center text-center py-10">
              <Wine size={28} className="text-oro/40 mb-4" />
              <p className="text-panna/40 text-sm max-w-xs">
                Il consiglio apparirà qui, parola per parola. Come al bancone di un'enoteca.
              </p>
            </div>
          )}
          {loading && !result && (
            <div className="flex gap-2 justify-center py-16">
              <span className="h-2 w-2 rounded-full bg-oro animate-pulse" />
              <span className="h-2 w-2 rounded-full bg-oro animate-pulse [animation-delay:150ms]" />
              <span className="h-2 w-2 rounded-full bg-oro animate-pulse [animation-delay:300ms]" />
            </div>
          )}
          {result && (
            <>
              <p className="text-panna/90 leading-relaxed whitespace-pre-wrap text-[15px]">{cleanMd(result)}</p>
              {!loading && leadState !== "done" && (
                <form onSubmit={submitLead} noValidate data-testid="consigliere-lead-form" className="mt-6 border-t border-oro/20 pt-6 space-y-3">
                  <div className="flex items-center gap-2">
                    <Sparkles size={15} className="text-oro" />
                    <p className="text-oro text-sm font-medium">Ti mando la guida gratuita + consigli come questo</p>
                  </div>
                  <p className="text-panna/50 text-xs">Lasciami nome ed email: ricevi subito i "7 Errori Comuni quando bevi vino" in PDF.</p>
                  <input data-testid="consigliere-lead-name" required minLength={2} placeholder="Il tuo nome"
                         value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })}
                         className="w-full rounded-lg px-4 py-3 bg-nero border border-panna/15 text-panna placeholder:text-panna/35 focus:outline-none focus:ring-2 focus:ring-oro/50 text-sm" />
                  <input data-testid="consigliere-lead-email" required type="email" placeholder="La tua email"
                         value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })}
                         className="w-full rounded-lg px-4 py-3 bg-nero border border-panna/15 text-panna placeholder:text-panna/35 focus:outline-none focus:ring-2 focus:ring-oro/50 text-sm" />
                  <label className="flex items-start gap-2 text-panna/50 text-xs cursor-pointer">
                    <input type="checkbox" data-testid="consigliere-lead-privacy" checked={lead.privacy}
                           onChange={(e) => setLead({ ...lead, privacy: e.target.checked })} className="mt-0.5 accent-bordeaux" />
                    <span>Accetto la <Link to="/privacy-policy" className="text-oro underline">privacy policy</Link> e di ricevere email da Dentro il Calice.</span>
                  </label>
                  {leadState === "error" && (
                    <p data-testid="consigliere-lead-error" className="text-oro-light text-xs">Controlla nome, email e l'accettazione della privacy, poi riprova.</p>
                  )}
                  <button type="submit" data-testid="consigliere-lead-submit" disabled={leadState === "loading"}
                          className="inline-flex items-center gap-2 rounded-full bg-bordeaux px-6 py-3 font-medium text-panna border border-oro/30 hover:bg-bordeaux-dark transition-colors disabled:opacity-60 text-sm">
                    {leadState === "loading" ? "Invio…" : "Mandami la guida gratuita"}
                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </form>
              )}
              {leadState === "done" && (
                <div data-testid="consigliere-lead-success" className="mt-6 border-t border-oro/20 pt-6">
                  <p className="text-oro text-sm font-medium">Perfetto, guida in arrivo!</p>
                  <p className="text-panna/60 text-xs mt-1">Nel frattempo puoi scaricarla subito qui sotto.</p>
                  <a href={`${API}/lead-magnet/download`} target="_blank" rel="noopener noreferrer" data-testid="consigliere-lead-download"
                     className="mt-3 inline-flex items-center gap-2 text-oro text-sm font-medium group">
                    Scarica la guida (PDF)
                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      </Reveal>
    </div>
  );
}
